import Enum from "enum-typescript"
import utils from '../utils'
import {ApiObject, Api} from "./api"
import {ISODate} from '../date'
import {ApiObjectFields, ApiObjectCalcField, EOFields, EOFieldsWithParent,
  Guid, ItemCode, WarehouseCode,
} from "./types"
import {TypeMap, assertTM, copyFilterEOValueToUpdateValue,
  GuidTypeMap, NullableStringTypeMap, StringTypeMap, NumberTypeMap, ISODateTypeMap,
  BooleanTypeMap, ItemCodeTypeMap, WarehouseCodeTypeMap,
  getCalcData, CSVListCalcTypeMap, SoLCalcTypeMap, WtLCalcTypeMap,
} from "./generaltypemaps"
import {getPurchaseOrderEOURL} from "../links"

export class POStatus extends Enum<number> {
  static OPEN = POStatus.createEnum(10)
  static PARTIAL = POStatus.createEnum(20)
  static CLOSED = POStatus.createEnum(30)
  static CANCELLED = POStatus.createEnum(40)
}

export const POStatusTypeMap: TypeMap<POStatus> = copyFilterEOValueToUpdateValue({
  fromEO: (data: number) => POStatus.valueOf(assertTM(
    d => POStatus.valueOf(d) !== undefined, data)),
  toEOFilterValue: (data: POStatus) => data.value.toString(),
})


export const PurchaseOrderFields = {
  purchaseOrderID: GuidTypeMap,
  orderNumber: NumberTypeMap,
  supplierName: StringTypeMap,
  description: NullableStringTypeMap,
  receiptStatus: POStatusTypeMap,
  orderDate: ISODateTypeMap,
  receiptDate: ISODateTypeMap,
  dropShipment: BooleanTypeMap,
  warehouseCode: WarehouseCodeTypeMap,
  warehouseDescription: StringTypeMap,
}

export const PurchaseOrderLineFields = {
  iD: GuidTypeMap,
  description: StringTypeMap,
  itemCode: ItemCodeTypeMap,
  itemDescription: StringTypeMap,
  notes: NullableStringTypeMap,
  receivedQuantity: NumberTypeMap,
  quantity: NumberTypeMap,
  receiptDate: ISODateTypeMap,
  lineNumber: NumberTypeMap,
}

const ShippingDoc = `
The shipping information (container numbers, ship numbers,
tracking numbers, etc) for the items in this line.
Through this information, the PO editor can easily update the expected
receipt date of a large number of Purchase Order Lines at once.

<example>PS327HGKD7632, CH67RTFGSKL, FX7483273443254</example>
`

const DSDoc = `
The presence of this dataName specifies that the purchase
order line is coupled one-on-one to a sales order line, and that
the shipment is a drop shipment (i.e. will not pass through the
warehouse mentioned on the purchase order, but is rather directly
shipped to the customer from the supplier).
(Note: there is also a built-in way in Exact to define drop shipments;
this method is also supported by NoseCC, however this method is more
flexible. The Exact method only allows a whole Purchase Order to be
a drop shipment, while this method allows this on a line-by-line base.)

It is required that the sales order line that this item points to,
is for the same itemCode, the same quantity, the same date AND
points back to this purchase order line through a
dropshipment dataName of its own (or else it will result
in an error).

<example>SO:123(5)</example>
`

const PORDoc = `
Whereas a drop shipment is a purchase order line and sales order
line in one (for something shipped directly to the customer), a
warehouse redirect connects a purchase order line with a
warehouse transfer line. In Exact a Purchae Order has a warehouse
where all lines are expected to be shipped to (e.g warehouse A).
If a certain line however gets shipped to a different warehouse
(e.g. warehouse B), you can make a Warehouse Transfer from A to B
and add connect the Purchase Order Lines to the Warehouse
Transfer Lines.

It is required that the warehouse transfer line that this item
points to,
is for the same itemCode, the same quantity, the same date (both
delivery date and receipt date) AND
points back to this purchase order line through a
po-redirect dataName of its own (or else it will
result in an error).

<example>WT:435(3)</example>
`


export const PurchaseOrderLineCalcFields = {
  shipping: {typemap: CSVListCalcTypeMap, default: new Array<string>(),
    documentation: ShippingDoc},
  dropShipment: {typemap: SoLCalcTypeMap, default: null, documentation: DSDoc},
  poRedirect: {typemap: WtLCalcTypeMap, default: null, documentation: PORDoc},
}

export class PurchaseOrder extends ApiObject
    implements ApiObjectFields<typeof PurchaseOrderFields> {
  readonly purchaseOrderID: Guid
  readonly orderNumber: number
  readonly supplierName: string
  readonly description: string | null
  readonly receiptStatus: POStatus
  readonly orderDate: ISODate
  readonly receiptDate: ISODate
  readonly dropShipment: boolean
  readonly warehouseCode: WarehouseCode
  readonly warehouseDescription: string

  readonly purchaseOrderLines: PurchaseOrderLine[]

  constructor(props: EOFields<typeof PurchaseOrder>) {
    super({})
    this.purchaseOrderID =
      PurchaseOrder.FIELDS.purchaseOrderID.fromEO(props.PurchaseOrderID)
    this.orderNumber = PurchaseOrder.FIELDS.orderNumber.fromEO(props.OrderNumber)
    this.supplierName = PurchaseOrder.FIELDS.supplierName.fromEO(props.SupplierName)
    this.description = PurchaseOrder.FIELDS.description.fromEO(props.Description)
    this.receiptStatus = PurchaseOrder.FIELDS.receiptStatus.fromEO(
      props.ReceiptStatus)
    this.orderDate = PurchaseOrder.FIELDS.orderDate.fromEO(props.OrderDate)
    this.receiptDate = PurchaseOrder.FIELDS.receiptDate.fromEO(props.ReceiptDate)
    this.dropShipment = PurchaseOrder.FIELDS.dropShipment.fromEO(props.DropShipment)
    this.warehouseCode =
      PurchaseOrder.FIELDS.warehouseCode.fromEO(props.WarehouseCode)
    this.warehouseDescription =
      PurchaseOrder.FIELDS.warehouseDescription.fromEO(props.WarehouseDescription)

    this.purchaseOrderLines = props.PurchaseOrderLines.map(
      eoPOL => new PurchaseOrderLine({...eoPOL, Parent: this})).sort(
      (pol1, pol2) => pol1.lineNumber - pol2.lineNumber)
  }

  static get URL(): string {
    return "purchaseorder/PurchaseOrders"
  }

  static get FIELDS(): typeof PurchaseOrderFields {
    return PurchaseOrderFields
  }

  static get EXPANDFIELDS(): typeof PurchaseOrderExpandFields {
    return PurchaseOrderExpandFields
  }

  getExactOnlineUrl(division: number): URL {
    return getPurchaseOrderEOURL(division, this.purchaseOrderID)
  }

  getId(): Guid {
    return this.purchaseOrderID
  }
}

export class PurchaseOrderLine extends ApiObject
    implements ApiObjectFields<typeof PurchaseOrderLineFields>,
    ApiObjectCalcField<typeof PurchaseOrderLineCalcFields>
    {
  readonly parent: PurchaseOrder

  readonly iD: Guid
  readonly description: string
  readonly itemCode: ItemCode
  readonly itemDescription: string
  readonly notes: string | null
  readonly receivedQuantity: number
  readonly quantity: number
  readonly receiptDate: ISODate
  readonly lineNumber: number

  readonly $calc: ApiObjectCalcField<typeof PurchaseOrderLineCalcFields>["$calc"]

  constructor(props: EOFieldsWithParent<typeof PurchaseOrderLine>) {
    super({})
    this.iD = PurchaseOrderLine.FIELDS.iD.fromEO(props.ID)
    this.description =
      PurchaseOrderLine.FIELDS.description.fromEO(props.Description)
    this.itemCode =
      PurchaseOrderLine.FIELDS.itemCode.fromEO(props.ItemCode)
    this.itemDescription =
      PurchaseOrderLine.FIELDS.itemDescription.fromEO(props.ItemDescription)
    this.notes = PurchaseOrderLine.FIELDS.notes.fromEO(props.Notes)
    this.receivedQuantity =
      PurchaseOrderLine.FIELDS.receivedQuantity.fromEO(props.ReceivedQuantity)
    this.quantity = PurchaseOrderLine.FIELDS.quantity.fromEO(props.Quantity)
    this.receiptDate =
      PurchaseOrderLine.FIELDS.receiptDate.fromEO(props.ReceiptDate)
    this.lineNumber = PurchaseOrderLine.FIELDS.lineNumber.fromEO(props.LineNumber)

    this.parent = props.Parent

    this.$calc = getCalcData(this.notes, PurchaseOrderLineCalcFields)
  }

  static get FIELDS(): typeof PurchaseOrderLineFields {
    return PurchaseOrderLineFields
  }

  static async updateMultipleReceiptDates(
      api: Api, division: number, isodate: ISODate,
      pols: readonly PurchaseOrderLine[]): Promise<readonly string[]> {
    const downloadURL = api.getXMLDownloadUrl(division, "PurchaseOrders")
    downloadURL.searchParams.set("Params_Status", [
      POStatus.OPEN, POStatus.PARTIAL].map(s => s.value.toString()).join(","))
    let PODomElements = Array<Element>()
    let tempxmldoc: Document | undefined = undefined
    while (true) {
      const response = await api.fetchWithAuthorization(downloadURL)
      tempxmldoc =
        new DOMParser().parseFromString(await response.text(), "text/xml")
      PODomElements = PODomElements.concat(Array.from(
                tempxmldoc.querySelectorAll("PurchaseOrders>PurchaseOrder")))
      const topic = tempxmldoc.querySelector('Topic[code="PurchaseOrders"]')
      if (topic === null) {
        console.log("nrPOs % pagesize seems to be 0")
        break
      }
      const count = parseInt(topic.getAttribute("count")!)
      const pagesize = parseInt(topic.getAttribute("pagesize")!)
      if (count < pagesize) {
        break;
      }
      const ts_d = topic.getAttribute("ts_d")!
      downloadURL.searchParams.set("TSPaging", ts_d)
    }
    if (!tempxmldoc) {
      throw new Error("Should have xml doc")
    }
    const xmldoc = tempxmldoc!
    xmldoc.querySelectorAll("eExact>*").forEach(el => el.remove())
    const purchaseOrdersEl = xmldoc.createElement("PurchaseOrders")
    xmldoc.querySelector("eExact")!.appendChild(purchaseOrdersEl)

    const polsByOrdernumber = new Map<number, PurchaseOrderLine[]>()
    pols.forEach(pol => {
      const ordernumber = pol.parent.orderNumber
      if (!polsByOrdernumber.has(ordernumber)) {
        polsByOrdernumber.set(ordernumber, [])
      }
      polsByOrdernumber.get(ordernumber)!.push(pol)
    })
    PODomElements.filter(poEl => {
      const ordernumber: number = parseInt(poEl.getAttribute("ordernumber")!)
      return polsByOrdernumber.has(ordernumber)
    }).forEach(poEl => {
      const ordernumber: number = parseInt(poEl.getAttribute("ordernumber")!)
      const pols = polsByOrdernumber.get(ordernumber)!
      polsByOrdernumber.delete(ordernumber)
      pols.forEach(pol => {
        const polEl =
          poEl.querySelector(`PurchaseOrderLine[line="${pol.lineNumber}"]`)
        utils.assert(
          polEl, `Cannot find PurchaseOrderLine[line="${pol.lineNumber}"]`)
        const notesFromXML = polEl.querySelector("Remarks")!.childNodes[0].nodeValue
        utils.assert(notesFromXML === pol.notes,
               `Notes don't match -- someone else edited the PO: `
                 + `'${notesFromXML}' != '${pol.notes}'`)
        polEl.querySelector("ReceiptDate")!.childNodes[0].nodeValue =
          isodate.toISOString();
      })
      purchaseOrdersEl.appendChild(poEl)
    })
    const newXml = new XMLSerializer().serializeToString(xmldoc)

    utils.assert(polsByOrdernumber.size === 0, "PurchaseOrders with numbers "
           + Array.from(polsByOrdernumber.keys()).map(n => n.toString()).join(", ")
           + " were not found in the xml")
    const uploadURL = api.getXMLUploadUrl(division, "PurchaseOrders")
    const uploadResponse = await api.fetchWithAuthorization(uploadURL, {
      headers: {"Content-Type": "text/xml"},
      method: "POST",
      body: newXml,
    })
    const uploadResponseXML = new DOMParser().parseFromString(
      await uploadResponse.text(), "text/xml")

    // XML schema says:
    // messagetype 0=Error, 1=Warning, 2=Message (Successful), 3=Fatal error
    const errorDescriptions: readonly string[] =
      [...uploadResponseXML.querySelectorAll('Message:not([type="2"])')].map(
        el => el.querySelector("Description")!.childNodes[0].nodeValue!)
    return errorDescriptions
  }
}

const PurchaseOrderExpandFields = {
  purchaseOrderLines: PurchaseOrderLine
} as const
